import Immutable from 'immutable';
import Resource from '../../../resource/Resource';

import {
  UPDATE_CERTIFICATION_PATHWAYS,
  CERTIFICATION_ASSESSMENT_REQUESTED_START,
  CERTIFICATION_ASSESSMENT_REQUESTED_SUCCESS,
  CERTIFICATION_ASSESSMENT_REQUESTED_FAILURE,
  CERTIFICATION_STUDENT_REGISTERED_SUCCESS,
  CERTIFICATION_ISSUED_SUCCESS,
  CERTIFICATION_REFRESHED_SUCCESS,
  CERTIFICATION_PROMPT_RETRY_SUCCESS,
  CERTIFICATION_UNDO_PROMPT_RETRY_SUCCESS,
} from './actions';

// Certication transitions
const ASSESSMENT_REQUESTED = 'assessment-requested';
const STUDENT_REGISTERED = 'student-registered';
const CERTIFICATE_ISSUED = 'certificate-issued';
const CERTIFICATE_REFRESHED = 'certificate-refreshed';
const ASSESSMENT_FAILED = 'assessment-failed';
const REASSESSMENT_REQUESTED = 'reassessment-requested';

// Certification triggers
const ASSESSMENT_REQUEST = 'assessment-request';
const USER_REGISTRATION_CONFIRMATION = 'user-registration-confirmation';
const PROMPT_RETRY = 'prompt-retry';
const UNDO_PROMPT_RETRY = 'undo-prompt-retry';
const ISSUE_CERTIFICATE = 'issue-certificate';
const REFRESH_CERTIFICATE = 'refresh-certificate';

export function updateCertificationPathways(certificationPathways) {
  return (dispatch) => {
    dispatch({
      type: UPDATE_CERTIFICATION_PATHWAYS,
      certificationPathways,
    });
  };
}

export function createCertificationPathway(certificationPathway) {
  return (dispatch) => {
    const creationData = certificationPathway.toJS();
    return new Promise((resolve, reject) =>
      Resource.create('certification_pathway', creationData, [
        'verifyState',
        'resource',
      ])
        .then((result) => {
          const savedCertificationPathway = result[1];
          dispatch({
            type: UPDATE_CERTIFICATION_PATHWAYS,
            certificationPathways: {
              [savedCertificationPathway.id]: savedCertificationPathway,
            },
          });
          resolve(Immutable.fromJS(savedCertificationPathway));
        })
        .fail(() => {
          reject(Error('Error creating certification pathway'));
        })
    );
  };
}

export function requestAssessment(assessmentRequest, certificationPathway) {
  return (dispatch) => {
    dispatch({
      type: CERTIFICATION_ASSESSMENT_REQUESTED_START,
      assessmentRequest,
    });

    Resource.update(
      'certification_pathway',
      certificationPathway.get('id'),
      'applyTransition',
      {
        transition: ASSESSMENT_REQUESTED,
        eventData: { assessmentRequest, triggered_by: ASSESSMENT_REQUEST },
      }
    )
      .then((result) => {
        if (result.success && result.certificationPathway) {
          setTimeout(() => {
            dispatch({
              type: CERTIFICATION_ASSESSMENT_REQUESTED_SUCCESS,
              certificationPathway: result.certificationPathway,
              assessmentRequest,
            });
          }, 300);
        } else {
          setTimeout(() => {
            dispatch({
              type: CERTIFICATION_ASSESSMENT_REQUESTED_FAILURE,
              assessmentRequest,
            });
          }, 300);
        }
      })
      .fail(() => {
        setTimeout(() => {
          dispatch({
            type: CERTIFICATION_ASSESSMENT_REQUESTED_FAILURE,
            assessmentRequest,
          });
        }, 300);
      });
  };
}

export function confirmRegistration(certificationPathway) {
  return (dispatch) => {
    return new Promise((resolve, reject) =>
      Resource.update(
        'certification_pathway',
        certificationPathway.get('id'),
        'applyTransition',
        {
          transition: STUDENT_REGISTERED,
          eventData: { triggered_by: USER_REGISTRATION_CONFIRMATION },
        }
      )
        .then((result) => {
          if (result.success && result.certificationPathway) {
            dispatch({
              type: CERTIFICATION_STUDENT_REGISTERED_SUCCESS,
              certificationPathway: result.certificationPathway,
            });
            resolve();
          } else {
            reject(Error());
          }
        })
        .fail(() => {
          reject(Error());
        })
    );
  };
}

export function issueCertificate(certificationPathway) {
  const errorTitle = 'Problem Issuing Certificate';
  const defaultErrorMessage =
    'There was a problem generating a certificate for this student';
  return (dispatch) => {
    return new Promise((resolve, reject) =>
      Resource.update(
        'certification_pathway',
        certificationPathway.get('id'),
        'applyTransition',
        {
          transition: CERTIFICATE_ISSUED,
          eventData: { triggered_by: ISSUE_CERTIFICATE },
        }
      )
        .then((result) => {
          if (result.success && result.certificationPathway) {
            dispatch({
              type: CERTIFICATION_ISSUED_SUCCESS,
              certificationPathway: result.certificationPathway,
            });
            resolve();
          } else {
            const errorMessage = result.error
              ? result.error
              : defaultErrorMessage;
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ errorTitle, errorMessage });
          }
        })
        .fail(() => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ errorTitle, errorMessage: defaultErrorMessage });
        })
    );
  };
}

export function refreshCertificate(certificationPathway) {
  const errorTitle = 'Problem Refreshing Certificate';
  const defaultErrorMessage =
    'There was a problem generating a certificate for this student';
  return (dispatch) => {
    // Return a new promise as the Resource.update has custom failure logic
    return new Promise((resolve, reject) =>
      Resource.update(
        'certification_pathway',
        certificationPathway.get('id'),
        'applyTransition',
        {
          transition: CERTIFICATE_REFRESHED,
          eventData: { triggered_by: REFRESH_CERTIFICATE },
        }
      )
        .then((result) => {
          if (result.success && result.certificationPathway) {
            dispatch({
              type: CERTIFICATION_REFRESHED_SUCCESS,
              certificationPathway: result.certificationPathway,
            });
            resolve();
          } else {
            const errorMessage = result.error
              ? result.error
              : defaultErrorMessage;
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ errorTitle, errorMessage });
          }
        })
        .fail(() => {
          // eslint-disable-next-line prefer-promise-reject-errors
          reject({ errorTitle, errorMessage: defaultErrorMessage });
        })
    );
  };
}

export function promptRetry(certificationPathway) {
  return (dispatch) => {
    return new Promise((resolve, reject) =>
      Resource.update(
        'certification_pathway',
        certificationPathway.get('id'),
        'applyTransition',
        {
          transition: ASSESSMENT_FAILED,
          eventData: { triggered_by: PROMPT_RETRY },
        }
      )
        .then((result) => {
          if (result.success && result.certificationPathway) {
            dispatch({
              type: CERTIFICATION_PROMPT_RETRY_SUCCESS,
              certificationPathway: result.certificationPathway,
            });
            resolve();
          } else {
            reject(Error());
          }
        })
        .fail(() => {
          reject(Error());
        })
    );
  };
}

export function undoPromptRetry(certificationPathway) {
  return (dispatch) => {
    return new Promise((resolve, reject) =>
      Resource.update(
        'certification_pathway',
        certificationPathway.get('id'),
        'applyTransition',
        {
          transition: REASSESSMENT_REQUESTED,
          eventData: { triggered_by: UNDO_PROMPT_RETRY },
        }
      )
        .then((result) => {
          if (result.success && result.certificationPathway) {
            dispatch({
              type: CERTIFICATION_UNDO_PROMPT_RETRY_SUCCESS,
              certificationPathway: result.certificationPathway,
            });
            resolve();
          } else {
            reject(Error());
          }
        })
        .fail(() => {
          reject(Error());
        })
    );
  };
}
